// REPORT-01 — controles de exportación. Una sola implementación para las tres superficies.
// La autorización EFECTIVA es del backend: aquí sólo se evita ofrecer lo que sería 403, y en el
// escenario DEMO los botones quedan deshabilitados (los reportes son de datos reales).
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { descargarReporte, imprimirReporte, puedeExportar } from "@/lib/reportes";
import { FileDown, Printer, Table2, Loader2 } from "lucide-react";

const hoy = () => new Date().toISOString().slice(0, 10);
const haceDias = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

function useExport() {
  const [ocupado, setOcupado] = useState(null);
  const correr = async (clave, fn) => {
    if (ocupado) return;
    setOcupado(clave);
    try { await fn(); } finally { setOcupado(null); }
  };
  return { ocupado, correr };
}

const CLS_INPUT = "rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700";

function Etiqueta({ children, htmlFor }) {
  return <label htmlFor={htmlFor} className="flex flex-col gap-1 text-xs font-medium text-slate-500">{children}</label>;
}

// ---------------------------------------------------------------- A · incidente
export function BotonesReporteIncidente({ casoId, demo }) {
  const { user } = useAuth();
  const { ocupado, correr } = useExport();
  if (!casoId || !puedeExportar(user)) return null;
  const path = `/reportes/incidente/${casoId}`;
  const cuerpo = { tamano: "carta" };
  return (
    <div data-testid="reporte-incidente-acciones" className="flex flex-wrap items-center gap-2">
      <Button size="sm" variant="outline" disabled={!!demo || !!ocupado}
        data-testid="reporte-incidente-pdf" className="rounded-lg"
        title={demo ? "Los reportes se generan con datos reales, no en el escenario de demostración." : "Descargar el reporte del incidente en PDF"}
        onClick={() => correr("pdf", () => descargarReporte(path, cuerpo, `incidente-${String(casoId).slice(0, 8)}.pdf`))}>
        {ocupado === "pdf" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileDown strokeWidth={1.7} className="mr-1 h-4 w-4" />}
        Descargar PDF
      </Button>
      <Button size="sm" variant="outline" disabled={!!demo || !!ocupado}
        data-testid="reporte-incidente-imprimir" className="rounded-lg"
        title={demo ? "No disponible en el escenario de demostración." : "Imprimir el reporte del incidente"}
        onClick={() => correr("print", () => imprimirReporte(path, cuerpo))}>
        {ocupado === "print" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Printer strokeWidth={1.7} className="mr-1 h-4 w-4" />}
        Imprimir
      </Button>
      {demo && <span className="text-xs text-slate-400">No disponible en demostración</span>}
    </div>
  );
}

// ---------------------------------------------------------------- B · bitácora por sitio
export function ExportarBitacora({ sitioId, sitioNombre, personaId, prioridad, clase, demo }) {
  const { user } = useAuth();
  const { ocupado, correr } = useExport();
  const [desde, setDesde] = useState(haceDias(29));
  const [hasta, setHasta] = useState(hoy());
  if (!puedeExportar(user)) return null;
  const cuerpo = (formato) => ({
    sitio_id: sitioId, desde, hasta, formato,
    ...(personaId ? { persona_id: personaId } : {}),
    ...(prioridad ? { prioridad } : {}),
    ...(clase ? { clase } : {}),
  });
  const sinSitio = !sitioId;
  return (
    <div data-testid="reporte-bitacora-panel"
      className="mb-4 rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-[#263B5D]">
      <p className="text-xs font-bold uppercase tracking-wider text-[#012472]">Exportar bitácora del sitio</p>
      <p className="mt-0.5 text-xs text-slate-500">
        Consulta TODOS los registros del periodo (no sólo los visibles). Un reporte = un sitio.
        Máximo 92 días.
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <Etiqueta htmlFor="rep-bit-desde">Desde
          <input id="rep-bit-desde" data-testid="reporte-bitacora-desde" type="date" value={desde}
            onChange={(e) => setDesde(e.target.value)} className={CLS_INPUT} />
        </Etiqueta>
        <Etiqueta htmlFor="rep-bit-hasta">Hasta
          <input id="rep-bit-hasta" data-testid="reporte-bitacora-hasta" type="date" value={hasta}
            onChange={(e) => setHasta(e.target.value)} className={CLS_INPUT} />
        </Etiqueta>
        <Button size="sm" variant="outline" className="rounded-lg" disabled={sinSitio || !!demo || !!ocupado}
          data-testid="reporte-bitacora-pdf"
          onClick={() => correr("pdf", () => descargarReporte("/reportes/bitacora", cuerpo("pdf"), `bitacora-${desde}-a-${hasta}.pdf`))}>
          {ocupado === "pdf" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileDown strokeWidth={1.7} className="mr-1 h-4 w-4" />}
          PDF
        </Button>
        <Button size="sm" variant="outline" className="rounded-lg" disabled={sinSitio || !!demo || !!ocupado}
          data-testid="reporte-bitacora-csv"
          onClick={() => correr("csv", () => descargarReporte("/reportes/bitacora", cuerpo("csv"), `bitacora-${desde}-a-${hasta}.csv`))}>
          {ocupado === "csv" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Table2 strokeWidth={1.7} className="mr-1 h-4 w-4" />}
          CSV
        </Button>
        <Button size="sm" variant="outline" className="rounded-lg" disabled={sinSitio || !!demo || !!ocupado}
          data-testid="reporte-bitacora-imprimir"
          onClick={() => correr("print", () => imprimirReporte("/reportes/bitacora", cuerpo("pdf")))}>
          {ocupado === "print" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Printer strokeWidth={1.7} className="mr-1 h-4 w-4" />}
          Imprimir
        </Button>
        <p data-testid="reporte-bitacora-aviso" className="text-xs text-slate-500">
          {demo ? "No disponible en el escenario de demostración."
            : sinSitio ? "Elige un sitio en el filtro para exportar."
              : `Sitio: ${sitioNombre || sitioId}`}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- C · operativo por sitio
export function ExportarReporteSitio({ sitioId, sitioNombre }) {
  const { user } = useAuth();
  const { ocupado, correr } = useExport();
  const [desde, setDesde] = useState(haceDias(29));
  const [hasta, setHasta] = useState(hoy());
  if (!puedeExportar(user) || !sitioId) return null;
  const cuerpo = { sitio_id: sitioId, desde, hasta, tamano: "carta" };
  return (
    <div data-testid="reporte-sitio-panel" className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <p className="text-xs font-bold uppercase tracking-wider text-[#012472]">Reporte operativo del sitio</p>
      <p className="mt-0.5 text-xs text-slate-500">
        Casos por tipo, prioridad, estado y equipo de origen, con acciones y tiempos medibles del
        periodo. Se cuentan casos, no sucesos supuestos. Máximo 366 días.
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <Etiqueta htmlFor="rep-sitio-desde">Desde
          <input id="rep-sitio-desde" data-testid="reporte-sitio-desde" type="date" value={desde}
            onChange={(e) => setDesde(e.target.value)} className={CLS_INPUT} />
        </Etiqueta>
        <Etiqueta htmlFor="rep-sitio-hasta">Hasta
          <input id="rep-sitio-hasta" data-testid="reporte-sitio-hasta" type="date" value={hasta}
            onChange={(e) => setHasta(e.target.value)} className={CLS_INPUT} />
        </Etiqueta>
        <Button size="sm" variant="outline" className="rounded-lg" disabled={!!ocupado}
          data-testid="reporte-sitio-pdf"
          onClick={() => correr("pdf", () => descargarReporte("/reportes/sitio", cuerpo, `operativo-${desde}-a-${hasta}.pdf`))}>
          {ocupado === "pdf" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileDown strokeWidth={1.7} className="mr-1 h-4 w-4" />}
          Descargar PDF
        </Button>
        <Button size="sm" variant="outline" className="rounded-lg" disabled={!!ocupado}
          data-testid="reporte-sitio-imprimir"
          onClick={() => correr("print", () => imprimirReporte("/reportes/sitio", cuerpo))}>
          {ocupado === "print" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Printer strokeWidth={1.7} className="mr-1 h-4 w-4" />}
          Imprimir
        </Button>
        <p className="text-xs text-slate-500">{sitioNombre ? `Sitio: ${sitioNombre}` : null}</p>
      </div>
    </div>
  );
}
